import axios from "axios";
import create from "zustand";
import { persist } from "zustand/middleware";
import { supabase } from "../supabase/supabase.config";

// Access the auth store to get the JWT token
const useAuthStore = create<any>(
  persist(
    (set) => ({
      user: null,
      setUser: (user: any) => set({ user }),
      clearUser: () => set({ user: null }),
    }),
    {
      name: "auth-token",
      getStorage: () => localStorage,
    }
  )
);

// Function to get the latest token - this ensures we always get the fresh token
const getLatestToken = () => {
  // Force a refresh from localStorage to ensure we have the latest data
  const storedData = localStorage.getItem("auth-token");
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      return parsedData.state?.user?.session?.access_token || 
             parsedData.state?.user?.access_token;
    } catch (e) {
      console.error("Error parsing auth token", e);
    }
  }
  // Fallback to the store state
  const user = useAuthStore.getState()?.user;
  return user?.session?.access_token || user?.access_token;
};

// Function to get the refresh token
const getRefreshToken = () => {
  const storedData = localStorage.getItem("auth-token");
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      return parsedData.state?.user?.refresh_token || 
             parsedData.state?.user?.session?.refresh_token;
    } catch (e) {
      console.error("Error parsing refresh token", e);
    }
  }
  // Fallback to the store state
  const user = useAuthStore.getState()?.user;
  return user?.refresh_token || user?.session?.refresh_token;
};

// Function to refresh the session when token expires
const refreshAuthToken = async () => {
  try {
    const refresh_token = getRefreshToken();
    
    if (!refresh_token) {
      console.error("No refresh token available");
      return false;
    }

    console.log("Attempting to refresh session with refresh token");
    const { data, error } = await supabase.auth.refreshSession({ refresh_token });

    if (error) {
      console.error("Error refreshing session:", error);
      return false;
    }

    if (data?.session) {
      // Update the auth store with the new session data
      useAuthStore.getState().setUser(data.session);
      console.log("Session refreshed successfully");
      return true;
    }

    return false;
  } catch (error) {
    console.error("Unexpected error during token refresh:", error);
    return false;
  }
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_ROTOBRAIN_API_BASE_URL,
  transformRequest: [
    (data, headers): string => {
      return JSON.stringify(data);
    },
  ],
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(
  async (config) => {
    // Always get the latest token directly from localStorage/store
    const accessToken = getLatestToken();
    
    // Add the token to the Authorization header if it exists
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const {
      response: { status, config },
    } = error;
    if (status === 401 && config.headers.Authorization && !config._retry) {
      console.log("Received 401 error, attempting to refresh token");
      
      // Mark this request as being retried to prevent infinite loops
      config._retry = true;
      
      // Try to refresh the token
      const refreshSuccessful = await refreshAuthToken();
      
      if (refreshSuccessful) {
        // Get the new token and retry the original request
        const newToken = getLatestToken();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
          console.log("Retrying request with new token");
          return instance.request(config);
        }
      }
      
      console.error("Authentication error: Unable to refresh token");
      // If refresh fails, you might want to redirect to login or show an error
      // For now, we'll just throw the error
      throw new Error("Authentication failed - please log in again");
    } else {
      throw new Error(error.response.data.detail);
    }
  },
);

export default instance; 